export const SCHEDULE_IMPORT_FIELD_CONFIG = [
  {
    key: "scheduleID",
    label: "ID",
    aliases: ["ID", "Schedule ID"],
    required: true,
  },
  {
    key: "startDate",
    label: "Start Date",
    aliases: ["Start_Date", "Start Date"],
    required: true,
  },
  {
    key: "finishDate",
    label: "Finish Date",
    aliases: ["Finish_Date", "Finish Date"],
    required: false,
  },
  {
    key: "taskName",
    label: "Task Name",
    aliases: ["Task_Name", "Task Name"],
    required: true,
  },
  {
    key: "totalFloat",
    label: "Total Float",
    aliases: ["Total_Float", "Total Float"],
    required: false,
  },
  {
    key: "critical",
    label: "Critical",
    aliases: ["Critical"],
    required: false,
  },
];

export const SCHEDULE_IMPORT_COLUMNS = SCHEDULE_IMPORT_FIELD_CONFIG.map(
  (field) => field.aliases[0],
);

const SCHEDULE_IMPORT_FIELD_ALIASES = Object.fromEntries(
  SCHEDULE_IMPORT_FIELD_CONFIG.map((field) => [field.key, field.aliases]),
);

const SCHEDULE_IMPORT_DATE_FIELDS = new Set(["startDate", "finishDate"]);

function normalizeImportColumnKey(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function hasNonEmptyCellValue(value) {
  if (value instanceof Date) {
    return true;
  }

  return String(value ?? "").trim() !== "";
}

function findValueByAliases(row, aliases = []) {
  const rowEntries = Object.entries(row || {});

  for (const alias of aliases) {
    const normalizedAlias = normalizeImportColumnKey(alias);
    const matchedEntry = rowEntries.find(
      ([key]) => normalizeImportColumnKey(key) === normalizedAlias,
    );

    if (matchedEntry) {
      return matchedEntry[1] ?? "";
    }
  }

  return "";
}

function padDatePart(value) {
  return String(value).padStart(2, "0");
}

function formatDateParts(month, day, year) {
  return `${padDatePart(month)}-${padDatePart(day)}-${year}`;
}

function formatDateInstance(dateValue, useUtc = false) {
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
    return "";
  }

  const month = useUtc ? dateValue.getUTCMonth() + 1 : dateValue.getMonth() + 1;
  const day = useUtc ? dateValue.getUTCDate() : dateValue.getDate();
  const year = useUtc ? dateValue.getUTCFullYear() : dateValue.getFullYear();

  return formatDateParts(month, day, year);
}

function convertExcelSerialDateToString(value) {
  if (!Number.isFinite(value)) {
    return "";
  }

  const excelEpochUtc = Date.UTC(1899, 11, 30);
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const serialDays = Math.floor(value);
  const serialTimeMs = Math.round((value - serialDays) * millisecondsPerDay);
  const normalizedDate = new Date(
    excelEpochUtc + serialDays * millisecondsPerDay + serialTimeMs,
  );

  return formatDateInstance(normalizedDate, true);
}

export function normalizeScheduleDateValue(value) {
  if (value === null || value === undefined) {
    return "";
  }

  if (value instanceof Date) {
    return formatDateInstance(value);
  }

  if (typeof value === "number") {
    return convertExcelSerialDateToString(value);
  }

  const normalizedValue = String(value).trim();

  if (!normalizedValue) {
    return "";
  }

  const mmDdYyyyMatch = normalizedValue.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (mmDdYyyyMatch) {
    const [, month, day, year] = mmDdYyyyMatch;
    return formatDateParts(month, day, year);
  }

  const isoMatch = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return formatDateParts(month, day, year);
  }

  return normalizedValue;
}

export function normalizeScheduleCriticalValue(value) {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim().toLowerCase();
}

export function formatScheduleMappingSampleValue(value) {
  if (value === null || value === undefined) {
    return "";
  }

  if (value instanceof Date || typeof value === "number") {
    return normalizeScheduleDateValue(value);
  }

  return String(value).trim();
}

function findNormalizedValueByAliases(row, fieldKey) {
  const directFieldValue = row?.[fieldKey];
  const hasDirectFieldValue =
    directFieldValue !== undefined && directFieldValue !== null;

  if (hasDirectFieldValue) {
    if (SCHEDULE_IMPORT_DATE_FIELDS.has(fieldKey)) {
      return normalizeScheduleDateValue(directFieldValue);
    }

    if (fieldKey === "critical") {
      return normalizeScheduleCriticalValue(directFieldValue);
    }

    return directFieldValue;
  }

  const rawValue = findValueByAliases(
    row,
    SCHEDULE_IMPORT_FIELD_ALIASES[fieldKey] || [],
  );

  if (SCHEDULE_IMPORT_DATE_FIELDS.has(fieldKey)) {
    return normalizeScheduleDateValue(rawValue);
  }

  if (fieldKey === "critical") {
    return normalizeScheduleCriticalValue(rawValue);
  }

  return rawValue;
}

function normalizeErrorMessages(row = {}) {
  const rawErrors =
    row.errorMessages ?? row.errorMessage ?? row.errors ?? row.issueMessages;

  if (Array.isArray(rawErrors)) {
    return rawErrors
      .flatMap((errorValue) =>
        Array.isArray(errorValue) ? errorValue : [errorValue],
      )
      .map((errorValue) => String(errorValue || "").trim())
      .filter(Boolean);
  }

  if (rawErrors && typeof rawErrors === "object") {
    return Object.values(rawErrors)
      .map((errorValue) => String(errorValue || "").trim())
      .filter(Boolean);
  }

  if (rawErrors) {
    return [String(rawErrors).trim()].filter(Boolean);
  }

  return [];
}

export function normalizeScheduleImportRow(row = {}, index = 0) {
  return {
    ...row,
    lineCnt:
      row.lineCnt ??
      row["Line #"] ??
      row.lineNumber ??
      row.rowNumber ??
      index + 2,
    scheduleID: findNormalizedValueByAliases(row, "scheduleID"),
    startDate: findNormalizedValueByAliases(row, "startDate"),
    finishDate: findNormalizedValueByAliases(row, "finishDate"),
    taskName: findNormalizedValueByAliases(row, "taskName"),
    totalFloat: findNormalizedValueByAliases(row, "totalFloat"),
    critical: findNormalizedValueByAliases(row, "critical"),
    errorMessages: normalizeErrorMessages(row),
  };
}

export function normalizeScheduleImportRows(rows) {
  return Array.isArray(rows)
    ? rows.map((row, index) => normalizeScheduleImportRow(row, index))
    : [];
}

export function mapScheduleImportRowForApi(row = {}) {
  return {
    ID: row.scheduleID || "",
    Start_Date: row.startDate || "",
    Finish_Date: row.finishDate || "",
    Task_Name: row.taskName || "",
    Total_Float: row.totalFloat || "",
    Critical: normalizeScheduleCriticalValue(row.critical),
  };
}

function getMappedColumnValue(row = [], mappedColumnIndex) {
  if (
    mappedColumnIndex === undefined ||
    mappedColumnIndex === null ||
    mappedColumnIndex === ""
  ) {
    return "";
  }

  const columnIndex = Number(mappedColumnIndex);

  if (Array.isArray(row)) {
    if (!Number.isInteger(columnIndex) || columnIndex < 0) {
      return "";
    }

    return row[columnIndex] ?? "";
  }

  if (row && typeof row === "object") {
    if (Object.prototype.hasOwnProperty.call(row, mappedColumnIndex)) {
      return row[mappedColumnIndex] ?? "";
    }

    const normalizedMappedColumnKey = normalizeImportColumnKey(mappedColumnIndex);
    const matchedEntry = Object.entries(row).find(
      ([key]) => normalizeImportColumnKey(key) === normalizedMappedColumnKey,
    );

    return matchedEntry?.[1] ?? "";
  }

  return "";
}

function getMaxColumnCount(headerRow = [], dataRows = []) {
  return [headerRow, ...dataRows].reduce((maxColumnCount, row) => {
    return Array.isArray(row)
      ? Math.max(maxColumnCount, row.length)
      : maxColumnCount;
  }, 0);
}

export function buildScheduleImportColumnOptions(
  headerRow = [],
  dataRows = [],
) {
  const maxColumnCount = getMaxColumnCount(headerRow, dataRows);

  return Array.from({ length: maxColumnCount }, (_, index) => {
    const rawHeader = String(headerRow?.[index] ?? "").trim();
    const sampleValue = Array.isArray(dataRows)
      ? (dataRows.find((row) => hasNonEmptyCellValue(row?.[index]))?.[index] ??
        "")
      : "";

    return {
      value: String(index),
      index,
      rawHeader,
      headerLabel: rawHeader || `Column ${index + 1}`,
      sampleValue,
    };
  });
}

export function buildScheduleImportColumnOptionsFromRows(
  rows = [],
  preferredHeaderOrder = [],
) {
  const rowsByNormalizedHeader = (Array.isArray(rows) ? rows : []).reduce(
    (headers, row) => {
      if (!row || typeof row !== "object" || Array.isArray(row)) {
        return headers;
      }

      Object.keys(row).forEach((key) => {
        const normalizedHeaderKey = normalizeImportColumnKey(key);

        if (normalizedHeaderKey && !headers.has(normalizedHeaderKey)) {
          headers.set(normalizedHeaderKey, key);
        }
      });

      return headers;
    },
    new Map(),
  );
  const preferredHeadersByNormalizedHeader = (
    Array.isArray(preferredHeaderOrder) ? preferredHeaderOrder : []
  ).reduce((headers, headerKey) => {
    const normalizedHeaderKey = normalizeImportColumnKey(headerKey);

    if (normalizedHeaderKey && !headers.has(normalizedHeaderKey)) {
      headers.set(normalizedHeaderKey, String(headerKey));
    }

    return headers;
  }, new Map());
  const normalizedHeaderKeys = [
    ...preferredHeadersByNormalizedHeader.keys(),
    ...Array.from(rowsByNormalizedHeader.keys()).filter(
      (headerKey) => !preferredHeadersByNormalizedHeader.has(headerKey),
    ),
  ];

  return normalizedHeaderKeys.map((normalizedHeaderKey) => {
    const sourceHeader =
      rowsByNormalizedHeader.get(normalizedHeaderKey) ||
      preferredHeadersByNormalizedHeader.get(normalizedHeaderKey);
    const rawHeader = String(sourceHeader || "").trim();
    const sampleRow = Array.isArray(rows)
      ? rows.find((row) =>
          hasNonEmptyCellValue(getMappedColumnValue(row, sourceHeader)),
        )
      : null;
    const sampleValue = getMappedColumnValue(sampleRow, sourceHeader);

    return {
      value: sourceHeader,
      index: sourceHeader,
      rawHeader,
      headerLabel: rawHeader,
      sampleValue,
    };
  });
}

export function buildInitialScheduleColumnMapping(columnOptions = []) {
  const usedColumns = new Set();

  return SCHEDULE_IMPORT_FIELD_CONFIG.reduce((mapping, field) => {
    const matchedColumn = columnOptions.find((columnOption) => {
      if (usedColumns.has(columnOption.value) || !columnOption.rawHeader) {
        return false;
      }

      return field.aliases.some(
        (alias) =>
          normalizeImportColumnKey(alias) ===
          normalizeImportColumnKey(columnOption.rawHeader),
      );
    });

    if (matchedColumn) {
      mapping[field.key] = matchedColumn.value;
      usedColumns.add(matchedColumn.value);
    } else {
      mapping[field.key] = "";
    }

    return mapping;
  }, {});
}

export function validateScheduleColumnMapping(columnMapping = {}) {
  const missingFields = SCHEDULE_IMPORT_FIELD_CONFIG.filter((field) => {
    if (!field.required) {
      return false;
    }

    const mappedValue = columnMapping?.[field.key];
    return (
      mappedValue === undefined || mappedValue === null || mappedValue === ""
    );
  }).map((field) => field.label);

  const mappedValues = SCHEDULE_IMPORT_FIELD_CONFIG.map(
    (field) => columnMapping?.[field.key],
  ).filter((value) => value !== undefined && value !== null && value !== "");

  const hasDuplicateMappings =
    new Set(mappedValues).size !== mappedValues.length;

  return {
    isValid: missingFields.length === 0 && !hasDuplicateMappings,
    missingFields,
    hasDuplicateMappings,
  };
}

export function buildScheduleImportRowsFromColumnMapping(
  dataRows = [],
  columnMapping = {},
) {
  return Array.isArray(dataRows)
    ? dataRows.map((row, index) =>
        normalizeScheduleImportRow({
          lineCnt: index + 2,
          scheduleID: getMappedColumnValue(row, columnMapping.scheduleID),
          startDate: getMappedColumnValue(row, columnMapping.startDate),
          finishDate: getMappedColumnValue(row, columnMapping.finishDate),
          taskName: getMappedColumnValue(row, columnMapping.taskName),
          totalFloat: getMappedColumnValue(row, columnMapping.totalFloat),
          critical: getMappedColumnValue(row, columnMapping.critical),
        }),
      )
    : [];
}

export function validateScheduleImportHeaders(headerRow = []) {
  const sanitizedHeaderRow = (Array.isArray(headerRow) ? headerRow : []).filter(
    (columnName) => String(columnName || "").trim() !== "",
  );
  const normalizedExpectedColumns = new Map(
    SCHEDULE_IMPORT_COLUMNS.map((columnName) => [
      normalizeImportColumnKey(columnName),
      columnName,
    ]),
  );
  const normalizedActualHeaders = sanitizedHeaderRow.map((columnName) =>
    normalizeImportColumnKey(columnName),
  );

  const missingColumns = SCHEDULE_IMPORT_COLUMNS.filter(
    (columnName) =>
      !normalizedActualHeaders.includes(normalizeImportColumnKey(columnName)),
  );
  const unexpectedColumns = sanitizedHeaderRow.filter(
    (columnName) =>
      !normalizedExpectedColumns.has(normalizeImportColumnKey(columnName)),
  );

  return {
    isValid: missingColumns.length === 0 && unexpectedColumns.length === 0,
    missingColumns,
    unexpectedColumns,
  };
}
